package ai.wagemix.analytics

import ai.wagemix.logic.analytics.WMAnalyticsVisit
import ai.wagemix.logic.analytics.WMLoginSession
import ai.wagemix.logic.analytics.WMUtm
import kotlinx.browser.localStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Instant

/**
 * What analytics keeps in the browser's `localStorage`, so that it survives reloads, tabs and browser restarts: the
 * login session (planning/analytics-plan.md §3), the current visit, and the last profile sent to OpenPanel.
 *
 * Storage can be unavailable (a private window, blocked site data) or hold a value an older build wrote: every read
 * then answers null and every write is skipped, so analytics degrades to per-page state and never breaks the page.
 */
internal interface WMAnalyticsBrowserStore {

    /** The stored login session, or null. */
    fun loginSession(): WMLoginSession?

    /** Stores [session], replacing any other. */
    fun storeLoginSession(session: WMLoginSession)

    /** Removes the stored login session. */
    fun clearLoginSession()

    /** The stored visit, or null. */
    fun visit(): WMAnalyticsVisit?

    /** Stores [visit]. */
    fun storeVisit(visit: WMAnalyticsVisit)

    /** The fingerprint of the profile last sent to OpenPanel, or null. */
    fun sentProfileFingerprint(): String?

    /** Stores the fingerprint of the profile just sent, or removes it when [fingerprint] is null. */
    fun storeSentProfileFingerprint(fingerprint: String?)
}

internal fun WMAnalyticsBrowserStore(): WMAnalyticsBrowserStore = WMLocalStorageAnalyticsStore()

private const val KEY_LOGIN_SESSION = "ai.wagemix.analytics.loginSession"
private const val KEY_VISIT = "ai.wagemix.analytics.visit"
private const val KEY_SENT_PROFILE = "ai.wagemix.analytics.sentProfile"

private class WMLocalStorageAnalyticsStore: WMAnalyticsBrowserStore {

    private val json = Json { ignoreUnknownKeys = true }

    override fun loginSession(): WMLoginSession? {
        val stored = read(KEY_LOGIN_SESSION) ?: return null
        val wire = decodeOrNull { json.decodeFromString(LoginSessionWire.serializer(), stored) } ?: return null
        return WMLoginSession(uid = wire.uid, id = wire.id, startedAt = Instant.fromEpochMilliseconds(wire.startedAtMs))
    }

    override fun storeLoginSession(session: WMLoginSession) {
        val startedAtMs = session.startedAt.toEpochMilliseconds()
        val wire = LoginSessionWire(uid = session.uid, id = session.id, startedAtMs = startedAtMs)
        write(KEY_LOGIN_SESSION, json.encodeToString(LoginSessionWire.serializer(), wire))
    }

    override fun clearLoginSession() = remove(KEY_LOGIN_SESSION)

    override fun visit(): WMAnalyticsVisit? {
        val stored = read(KEY_VISIT) ?: return null
        val wire = decodeOrNull { json.decodeFromString(VisitWire.serializer(), stored) } ?: return null
        val utm = WMUtm(
            source = wire.utmSource,
            medium = wire.utmMedium,
            campaign = wire.utmCampaign,
            content = wire.utmContent,
            term = wire.utmTerm,
        )
        val lastActivityAt = Instant.fromEpochMilliseconds(wire.lastActivityAtMs)
        return WMAnalyticsVisit(lastActivityAt = lastActivityAt, screen = wire.screen, utm = utm)
    }

    override fun storeVisit(visit: WMAnalyticsVisit) {
        val wire = VisitWire(
            lastActivityAtMs = visit.lastActivityAt.toEpochMilliseconds(),
            screen = visit.screen,
            utmSource = visit.utm.source,
            utmMedium = visit.utm.medium,
            utmCampaign = visit.utm.campaign,
            utmContent = visit.utm.content,
            utmTerm = visit.utm.term,
        )
        write(KEY_VISIT, json.encodeToString(VisitWire.serializer(), wire))
    }

    override fun sentProfileFingerprint(): String? = read(KEY_SENT_PROFILE)

    override fun storeSentProfileFingerprint(fingerprint: String?) {
        if (fingerprint == null) {
            remove(KEY_SENT_PROFILE)
        } else {
            write(KEY_SENT_PROFILE, fingerprint)
        }
    }

    private fun read(key: String): String? {
        return try {
            localStorage.getItem(key)
        } catch (e: Throwable) {
            null
        }
    }

    private fun write(key: String, value: String) {
        try {
            localStorage.setItem(key, value)
        } catch (e: Throwable) {
            // Storage is unavailable or full; the value lives for this page only.
        }
    }

    private fun remove(key: String) {
        try {
            localStorage.removeItem(key)
        } catch (e: Throwable) {
            // Storage is unavailable; nothing was stored.
        }
    }

    private inline fun <T> decodeOrNull(decode: () -> T): T? {
        return try {
            decode()
        } catch (e: Exception) {
            null
        }
    }
}

@Serializable
private data class LoginSessionWire(val uid: String, val id: String, val startedAtMs: Long)

@Serializable
private data class VisitWire(
    val lastActivityAtMs: Long,
    val screen: String,
    val utmSource: String? = null,
    val utmMedium: String? = null,
    val utmCampaign: String? = null,
    val utmContent: String? = null,
    val utmTerm: String? = null,
)
